/** Reusable audio pool - never allocate a new Audio() per click. */

const POOL_SIZE = 8;

let muted = false;

export function setMuted(value) {
  muted = Boolean(value);
}

export function isMuted() {
  return muted;
}

class AudioPool {
  constructor(src, size = POOL_SIZE) {
    this.nodes = Array.from({ length: size }, () => {
      const a = new Audio(src);
      a.preload = "auto";
      a.volume = 0.45;
      return a;
    });
    this.index = 0;
    this.unlocked = false;
  }

  unlock() {
    if (this.unlocked) return;
    this.unlocked = true;
    // Warm one node after a user gesture so subsequent plays aren't blocked.
    const probe = this.nodes[0];
    const prev = probe.volume;
    probe.volume = 0;
    probe
      .play()
      .then(() => {
        probe.pause();
        probe.currentTime = 0;
        probe.volume = prev;
      })
      .catch(() => {});
  }

  play(rate = 1) {
    if (muted) return;
    const node = this.nodes[this.index];
    this.index = (this.index + 1) % this.nodes.length;
    try {
      node.pause();
      node.currentTime = 0;
      node.playbackRate = rate;
      node.play().catch(() => {});
    } catch {
      /* ignore autoplay races */
    }
  }
}

let popPool = null;
let audioCtx = null;

function getPopPool() {
  if (!popPool) popPool = new AudioPool("/pop.mp3");
  return popPool;
}

function getCtx() {
  if (!audioCtx) {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (Ctx) audioCtx = new Ctx();
  }
  return audioCtx;
}

export function unlockAudio() {
  getPopPool().unlock();
  const ctx = getCtx();
  if (ctx?.state === "suspended") ctx.resume().catch(() => {});
}

export function playClick(variant = 0) {
  if (muted) return;
  // Streak hits a brighter pitch; otherwise slight variation by strike count.
  if (variant === "streak") {
    getPopPool().play(1.18);
    return;
  }
  const rate = 0.92 + (Number(variant) % 5) * 0.04;
  getPopPool().play(rate);
}

/** Short metallic stinger for unlock climax - no extra asset files. */
export function playUnlock() {
  if (muted) return;
  const ctx = getCtx();
  if (!ctx) return;
  if (ctx.state === "suspended") ctx.resume().catch(() => {});

  const now = ctx.currentTime;
  const master = ctx.createGain();
  master.gain.setValueAtTime(0.0001, now);
  master.gain.exponentialRampToValueAtTime(0.22, now + 0.02);
  master.gain.exponentialRampToValueAtTime(0.0001, now + 0.55);
  master.connect(ctx.destination);

  ;[220, 330, 495].forEach((freq, i) => {
    const osc = ctx.createOscillator();
    const g = ctx.createGain();
    osc.type = i === 2 ? "triangle" : "sine";
    osc.frequency.setValueAtTime(freq, now);
    g.gain.setValueAtTime(0.0001, now);
    g.gain.exponentialRampToValueAtTime(0.35 / (i + 1), now + 0.03 + i * 0.04);
    g.gain.exponentialRampToValueAtTime(0.0001, now + 0.45 + i * 0.05);
    osc.connect(g);
    g.connect(master);
    osc.start(now + i * 0.03);
    osc.stop(now + 0.6);
  });
}
